import { LiquidVariant } from "@sections/product";
import { sendAddToCart, fetchCartData } from "../helpers/cart";
import { formatMoney } from "@helpers/currency";

// Define the interface for TypeScript, if you're using it. Otherwise, ignore this part in plain JS.
export interface IQuickShopButton extends HTMLElement {
  addToCartButton: HTMLButtonElement | null;
  closeQuickShopButton: HTMLButtonElement | null;
  quantityInputs: NodeListOf<HTMLInputElement> | undefined;
  quickShopModal: HTMLElement | null;
  atcButton: HTMLElement | null;
  soldOutToggles: HTMLElement[];
  soldOutButton: HTMLElement | null;
  bisButton: HTMLElement | null;
}

interface QuickShopVariant extends LiquidVariant {
  title: string;
  price_per_can: string;
  real_compare_at_price: string;
  canAmount: number;
  compare_at_price: string;
  available: boolean;
  bis_enabled: boolean;
  inventory_quantity: number;
  availabilityClass: string;
  bundle_item_inventory: number;
  quantityAvailable: number;
  bundle_item: BundleItem;
  pricePerCan: number;
  price: string;
  millilitres: string;
  milText: string;
  hasBundleAndQuantity: boolean;
  max_quantity?: number;
  min_quantity?: number;
  pfand: string;
}

interface BundleItem {
  available: boolean;
  title: string;
  description: string;
  content: string;
  variants: {
    id: string;
    title: string;
    price: string;
    compare_at_price: string;
    available: boolean;
    canAmount: string;
    millilitres: string;
  }[];
}

class QuickShopButton extends HTMLElement implements IQuickShopButton {
  public addToCartButton: HTMLButtonElement | null = null;
  public closeQuickShopButton: HTMLButtonElement | null = null;
  public quantityInputs: NodeListOf<HTMLInputElement> | undefined;
  public quickShopModal: HTMLElement | null = null;
  public atcButton: HTMLElement | null = null;
  public soldOutToggles: HTMLElement[] = [];
  public soldOutButton: HTMLElement | null = null;
  public bisButton: HTMLElement | null = null;
  public bisPopupInitial: HTMLElement | null = null;
  public bisPopup: HTMLElement | null = null;

  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", this.openQuickShop.bind(this));
  }

  async openQuickShop() {
    this.quickShopModal = document.createElement("div");
    const cartData = await fetchCartData();

    const quickShopProduct = JSON.parse(
      this.getAttribute("data-product") || "{}",
    );

    // Check if any variants would exceed max quantity with current cart
    let allVariantsMaxedOut = true;

    for (const variant of quickShopProduct.variants) {
      const variantId = variant.bundle_item
        ? variant.bundle_item.variants[0].id
        : variant.id;
      const variantInCart = cartData.items.find((item) => {
        if (parseInt(item.variant_id) === parseInt(variantId)) {
          return true;
        }
        return false;
      });
      if (variantInCart) {
        if (variant.max_quantity) {
          variant.max_quantity = variant.max_quantity - variantInCart.quantity;
          // If there's still room for more, not all variants are maxed out
          if (variant.max_quantity > 0) {
            allVariantsMaxedOut = false;
          }

          // If already at max, don't use min_quantity
          if (variant.max_quantity <= 0) {
            variant.min_quantity = 0;
          } else if (
            variant.min_quantity &&
            variant.min_quantity > variant.max_quantity
          ) {
            // If min_quantity would exceed remaining max, set it to 0
            variant.min_quantity = 0;
          }
        } else if (variant.bundle_item_inventory) {
          variant.max_quantity =
            variant.bundle_item_inventory - variantInCart.quantity;
          if (variant.max_quantity > 0) {
            allVariantsMaxedOut = false;
          }

          // If already at max, don't use min_quantity
          if (variant.max_quantity <= 0) {
            variant.min_quantity = 0;
          } else if (
            variant.min_quantity &&
            variant.min_quantity > variant.max_quantity
          ) {
            // If min_quantity would exceed remaining max, set it to 0
            variant.min_quantity = 0;
          }
        } else {
          variant.max_quantity =
            variant.inventory_quantity - variantInCart.quantity;
          if (variant.max_quantity > 0) {
            allVariantsMaxedOut = false;
          }

          // If already at max, don't use min_quantity
          if (variant.max_quantity <= 0) {
            variant.min_quantity = 0;
          } else if (
            variant.min_quantity &&
            variant.min_quantity > variant.max_quantity
          ) {
            // If min_quantity would exceed remaining max, set it to 0
            variant.min_quantity = 0;
          }
        }
      } else {
        // If any variant isn't in the cart, it's not maxed out
        allVariantsMaxedOut = false;
      }
    }

    // Function to decrement quantity
    const quantityDecrement = (variantId: number): void => {
      const input = document.querySelector(
        `.quickshop__variants #variant-${variantId}`,
      ) as HTMLInputElement;

      if (input && parseInt(input.value) > 0) {
        input.value = (parseInt(input.value) - 1).toString();
        window.setTimeout(() => {
          updateSubTotal();
        }, 200);
      }
    };

    // Function to increment quantity
    const quantityIncrement = (variantId: number) => {
      const input = document.querySelector(
        `.quickshop__variants #variant-${variantId}`,
      ) as HTMLInputElement;
      const selectedVariant = quickShopProduct.variants.find(
        (variant: { id: number }) => variant.id === variantId,
      );

      // Use the max attribute value since it's already adjusted for cart items
      const maxQuantity = parseInt(input.max, 10);
      const currentValue = parseInt(input.value, 10);

      if (currentValue >= maxQuantity) {
        // Display error message when max quantity reached
        const variantGroup = input.closest(".product-swatches__form-group");
        if (variantGroup) {
          variantGroup.classList.add(
            "product-swatches__form-group--max-reached",
          );

          // Simplified error message
          const maxQuantityMessage = "You've reached the max quantity";

          // Check if an error message already exists and remove it
          const existingError = document.querySelector(
            ".product-swatches__max-qty-error",
          );
          if (existingError) {
            existingError.remove();
          }

          // Create and show the error message
          const maxError = document.createElement("span");
          maxError.classList.add("product-swatches__max-qty-error");
          maxError.innerHTML = maxQuantityMessage;

          // Style the error message to ensure visibility
          maxError.style.display = "block";
          maxError.style.color = "red";
          maxError.style.fontWeight = "bold";
          maxError.style.marginTop = "10px";
          maxError.style.marginBottom = "10px";
          maxError.style.position = "relative";
          maxError.style.zIndex = "10";

          // Insert directly after this variant group instead of before the button wrapper
          variantGroup.insertAdjacentElement("afterend", maxError);

          // Remove the error message after 3 seconds
          setTimeout(() => {
            variantGroup.classList.remove(
              "product-swatches__form-group--max-reached",
            );
            maxError.remove();
          }, 3000);
        }
        return;
      }

      if (parseInt(input.value) === 0) {
        input.value = "1";
        window.setTimeout(() => {
          updateSubTotal();
        }, 200);
      } else if (input && parseInt(input.value) < maxQuantity) {
        input.value = (parseInt(input.value) + 1).toString();
        window.setTimeout(() => {
          updateSubTotal();
        }, 200);
      }

      this.showATC();
    };

    // Function to update sub-total
    const updateSubTotal = () => {
      let subTotal = 0;
      document
        .querySelectorAll(".product-swatches__quantity input")
        .forEach((input) => {
          const inputElement = input as HTMLInputElement;
          const price = inputElement.dataset.price ?? "0";
          let priceNoCurrency = price.replace(",", ".");
          priceNoCurrency = priceNoCurrency.replace(",", ".");
          priceNoCurrency = priceNoCurrency.replace("€", "");
          priceNoCurrency = priceNoCurrency.replace("£", "");
          priceNoCurrency = priceNoCurrency.replace("$", "");
          subTotal +=
            parseInt(inputElement.value) * parseFloat(priceNoCurrency) * 100;
        });
      const quickshopSubtotal = document.getElementById("quickshop-subtotal");
      if (quickshopSubtotal) {
        quickshopSubtotal.innerText = "| " + formatMoney(subTotal);
      }
    };

    // Function to handle Add to Cart button
    const handleAddToCart = async () => {
      const cartData = await fetchCartData(); // Get the latest cart data
      const variantsToAdd: {
        id: string;
        quantity: number;
        properties: Record<string, string | string[] | Record<string, string>>;
        selling_plan: string | string[] | Record<string, string>;
        quantityAvailable: number;
        pre_order: boolean;
        pre_order_dispatch_eta: string;
      }[] = [];

      let exceedsMaxQuantity = false;
      let variantGroupWithError: HTMLElement | null = null;

      console.log("variantsToAdd", variantsToAdd, quickShopProduct)

      document
        .querySelectorAll(".product-swatches__quantity input")
        .forEach((input) => {
          const inputElement = input as HTMLInputElement;

          if (parseInt(inputElement.value) > 0) {
            const variantId = inputElement.id.split("-")[1].toString();
            const inputMax = parseInt(inputElement.max, 10);
            const addQuantity = parseInt(inputElement.value, 10);

            // Find the variant to get property info
            const matchingVariant = quickShopProduct.variants.find(
              (variant: { id: { toString: () => string }; title?: string }) =>
                variant.id.toString() === variantId,
            );

            // Since max quantity has already been adjusted for cart quantities,
            // we just need to check if the quantity to add exceeds max

            // Check if quantity to add exceeds the max available
            if (addQuantity > inputMax) {
              exceedsMaxQuantity = true;
              // Store the variant group for this input element to show error near it
              const closestGroup = inputElement.closest(
                ".product-swatches__form-group",
              );
              if (closestGroup instanceof HTMLElement) {
                variantGroupWithError = closestGroup;
              }
              return;
            }

            variantsToAdd.push({
              id: variantId,
              quantity: addQuantity,
              quantityAvailable: inputMax,
              properties: matchingVariant?.pfand
                ? { Pfand: matchingVariant.pfand }
                : {},
              selling_plan: "",
              pre_order: quickShopProduct.preOrder ? quickShopProduct.preOrder : false,
              pre_order_dispatch_eta: quickShopProduct.preOrderDispatchEta ? quickShopProduct.preOrderDispatchEta : '',
            });
          }
        });

      // If adding would exceed max quantity, show error and prevent add to cart
      if (exceedsMaxQuantity) {
        const errorMessage = "You've reached the max quantity";

        // Check if an error message already exists and remove it
        const existingError = document.querySelector(
          ".product-swatches__max-qty-error",
        );
        if (existingError) {
          existingError.remove();
        }

        // Create error message
        const maxError = document.createElement("span");
        maxError.classList.add("product-swatches__max-qty-error");

        // Style the error message to ensure visibility
        maxError.style.display = "block";
        maxError.style.color = "red";
        maxError.style.fontWeight = "bold";
        maxError.style.marginTop = "10px";
        maxError.style.marginBottom = "10px";
        maxError.style.position = "relative";
        maxError.style.zIndex = "10";
        maxError.innerHTML = errorMessage;

        // Place the error message after the variant that has the issue,
        // or after the variants container if we can't identify the specific variant
        if (variantGroupWithError) {
          variantGroupWithError.insertAdjacentElement("afterend", maxError);
        } else {
          const variantsContainer = document.querySelector(
            ".quickshop__variants",
          );
          if (variantsContainer) {
            variantsContainer.insertAdjacentElement("afterend", maxError);
          } else {
            // Fallback - just append to the quickshop content
            const quickshopContent = document.querySelector(
              ".quickshop__content",
            );
            quickshopContent?.appendChild(maxError);
          }
        }

        setTimeout(() => {
          maxError.remove();
        }, 3000);

        return; // Prevent adding to cart
      }

      if (variantsToAdd?.length) {
        sendAddToCart(variantsToAdd).then(() => {
          closeQuickShopButton();
        });
        return;
      }

      const currentOpenQuickShop = document.querySelector(".quickshop");

      if (!currentOpenQuickShop) {
        return;
      }

      currentOpenQuickShop.classList.add("quickshop--no-quantity");
    };

    // Function to close the QuickShop modal
    const closeQuickShopButton = () => {
      const quickshopElement = document.querySelector(".quickshop");
      if (quickshopElement) {
        quickshopElement.remove();
      }
    };

    let img = quickShopProduct.images[0].src;
    if (img === undefined) {
      img = quickShopProduct.images[0];
    }

    quickShopProduct.variants.forEach((variant: QuickShopVariant) => {
      if (variant.bundle_item) {
        const hasBundleAndQuantity = !variant.bundle_item.available;
        variant.availabilityClass =
          variant.bundle_item && hasBundleAndQuantity
            ? "product-swatches__form-group--sold-out"
            : "";
        variant.id = variant.bundle_item.variants[0].id;
        variant.quantityAvailable = variant.bundle_item_inventory;
        variant.available = variant.bundle_item.available;
      } else if (variant.available === false) {
        variant.availabilityClass = "product-swatches__form-group--sold-out";
        variant.hasBundleAndQuantity = false;
      }

      if (variant.millilitres) {
        variant.milText = `${window.themeVars.strings.product.from} ${variant.millilitres} / liter`;
      }
    });

    let soldOutButton = `
    <button
      type="button"
      class="quickshop__atc-button btn btn-white btn-white--outline ${quickShopProduct.available ? "hidden" : ""}"
      data-sold-out-button
    >
      <span>
        ${window.themeVars.strings.product.soldOut}
      </span>
    </button>
  `;

    let soldOutButtonHidden = quickShopProduct.available ? "hidden" : "";

    if (
      quickShopProduct.variants.length === 1 &&
      quickShopProduct.variants[0].available === false
    ) {
      soldOutButtonHidden = "";
    }

    if (
      !quickShopProduct.available &&
      quickShopProduct.bis_enabled &&
      quickShopProduct.tags &&
      quickShopProduct.tags.includes("back-in-stock")
    ) {
      soldOutButton = `
       <button
            type="button"
            class="quickshop__atc-button btn btn-white btn-white--outline ${soldOutButtonHidden}"
            data-bis-trigger
          >
            <span>
              ${window.themeVars.strings.product.notifyMe}
            </span>
          </button>
    `;
    }

    // Creating QuickShop modal
    const quickShopContent = `
      <div class="quickshop">
        <div class="quickshop__overlay"></div>
        <div class="quickshop__content">
          <div class="quickshop__header">
            <img src="${img}" alt="${quickShopProduct.title}" />
            <h2 class="quickshop__title">${quickShopProduct.title}</h2>
            <button class="quickshop__close">
              ${window.themeVars.icons.close}
            </button>
          </div>
          <legend class="product-swatches__label">
            ${window.themeVars.strings.product.selectQuantities}
          </legend>
          ${
            window.themeVars.customer.staff &&
            !quickShopProduct.compare_at_price
              ? `
            <p class="quickshop__staff-member-badge staff-member-badge">
              <span>${window.themeVars.icons.checkmark}</span>
              ${window.themeVars.strings.product.staffDiscount}
            </p>
            `
              : window.themeVars.customer.efpMember &&
                  !quickShopProduct.compare_at_price
                ? `
            <p class="quickshop__efp-member-badge efp-member-badge">
              <span>${window.themeVars.icons.checkmark}</span>
              ${window.themeVars.strings.product.efpDiscount}
            </p>
            `
                : ""
          }
          <div class="quickshop__variants">
            ${quickShopProduct.variants
              .map(
                (variant: QuickShopVariant) => `
              <div
                class="product-swatches__form-group ${variant.availabilityClass}"
                ${quickShopProduct.bis_enabled && !variant.available ? "data-sold-out-toggle" : ""}
                ${quickShopProduct.bis_enabled && variant.bis_enabled ? "data-show-bis" : ""}
                data-variant-id="${variant.id}"
              >

                <div>
                  ${
                    variant.pfand
                      ? `
                          <span class="product-swatches__pfand">
                            Pfand: €${variant.pfand}
                          </span>
                        `
                      : ""
                  }
                  <label for="variant-${variant.id}">${variant.title}</label>
                </div>

                <div class="product-swatches__price-wrapper" data-compare-price="${variant.compare_at_price}">
                  <p class="product-swatches__price">
                    ${
                      variant.compare_at_price
                        ? `
                      <div>
                        <span class="visually-hidden">
                          ${window.themeVars.strings.product.price.sale}
                        </span>
                        <span>
                      ${
                        variant.real_compare_at_price
                          ? `
                        <span class="price price__regular price--on-sale">
                          ${variant.price}
                        </span>
                      `
                          : `
                        <span class="price price__regular">
                          ${variant.price}
                        </span>
                      `
                      }
                        <span>
                          <span class="price__compare-at compare-at-price">
                            ${variant.compare_at_price}
                          </span>
                        </span>
                      </div>
                    `
                        : `
                      <div class="price__regular">
                        <span class="visually-hidden">
                          ${window.themeVars.strings.product.price.regular}
                        </span>
                        <span class="price price__regular">
                          ${variant.price}
                        </span>
                      </div>
                    `
                    }
                  </p>
                   <span class="product-swatches__price__per-can">
                    ${
                      variant.price_per_can && !variant.millilitres
                        ? `
                      <span>
                        ${variant.price_per_can}
                        ${window.themeVars.strings.product.perCan}
                      </span>
                      `
                        : `

                   `
                    }
                  </span>
                </div>
                ${
                  quickShopProduct.bis_enabled &&
                  variant.bis_enabled &&
                  variant.available == false
                    ? `
                    <span class="product-swatches__bell-icon">
                      ${window.themeVars.icons.bell}
                    </span>
                  `
                    : `
                      <uwp-quantity-selector
                        data-max-quantity-message="You've reached the max quantity"
                        ${!variant.available && quickShopProduct.bis_enabled && variant.bis_enabled ? "style='display: none;'" : ""}
                      >
                        <div class="product-swatches__quantity quantity-selector quantity-selector--small">
                          <button type="button" class="minus" data-qty-button aria-label="Decrease quantity">
                            ${window.themeVars.icons.minus}
                          </button>
                          <input
                            type="number"
                            step="1"
                            min="${variant.min_quantity ?? 0}"
                            readonly
                            max="${variant.max_quantity ?? (variant.quantityAvailable || variant.inventory_quantity)}"
                            name="quantity"
                            value="${variant.min_quantity ?? 0}"
                            id="variant-${variant.id}"
                            class="quantity"
                            data-id="${variant.id}"
                            data-price="${variant.price}"
                            size="4"
                            data-qty-input
                            aria-label="Quantity input for ${variant.title}"
                            ${variant.pfand ? `data-pfand="${variant.pfand}"` : ""}
                          >

                          <button type="button" class="plus" data-qty-button aria-label="Increase quantity">
                            ${window.themeVars.icons.plus}
                          </button>
                        </div>
                      </uwp-quantity-selector>
                  `
                }
              </div>
            `,
              )
              .join("")}
          </div>

          <p class="quickshop__no-quantity-message">
            ${window.themeVars.strings.product.pleaseSelectQuantity}
          </p>

          <div class="quickshop__button-wrapper">
            <button
              type="submit"
              class="quickshop__atc-button btn btn-black ${!quickShopProduct.available || allVariantsMaxedOut ? "hidden" : ""}"
              id="multipleAtcButton"
            >
              <span>
                ${window.themeVars.strings.product.addToCart}
                <span id="quickshop-subtotal"></span>
              </span>
            </button>

            ${soldOutButton}
            ${
              allVariantsMaxedOut
                ? `
            <button
              type="button"
              class="quickshop__atc-button btn btn-white"
              style="border: 1px solid #000; color: #000;"
              disabled
            >
              <span>
                You've reached the max quantity
              </span>
            </button>
            `
                : ""
            }
          </div>
        </div>
      </div>
    `;
    this.quickShopModal.innerHTML = quickShopContent;
    document.body.appendChild(this.quickShopModal);

    // Attach event handlers
    this.quickShopModal
      ?.querySelector(".quickshop__close")
      ?.addEventListener("click", closeQuickShopButton);
    this.quickShopModal
      ?.querySelector(".quickshop__overlay")
      ?.addEventListener("click", closeQuickShopButton);
    const atcButton = this.quickShopModal?.querySelector(
      ".quickshop__atc-button",
    );
    if (atcButton) {
      atcButton.addEventListener("click", handleAddToCart);
    }

    quickShopProduct.variants.forEach(
      (variant: { id: number; price: number }) => {
        const decrementButton = this.quickShopModal?.querySelector(
          `.variant-${variant.id} .minus`,
        );
        const incrementButton = this.quickShopModal?.querySelector(
          `.variant-${variant.id} .plus`,
        );
        if (decrementButton) {
          decrementButton.addEventListener("click", () =>
            quantityDecrement(variant.id),
          );
        }
        if (incrementButton) {
          incrementButton.addEventListener("click", () =>
            quantityIncrement(variant.id),
          );
        }
      },
    );

    this.atcButton = this.quickShopModal.querySelector(
      "#multipleAtcButton",
    ) as HTMLElement;
    this.soldOutToggles = [
      ...this.quickShopModal.querySelectorAll("[data-sold-out-toggle]"),
    ] as HTMLElement[];
    this.soldOutButton = this.quickShopModal.querySelector(
      "[data-sold-out-button]",
    ) as HTMLElement;
    this.bisButton = this.quickShopModal.querySelector(
      "[data-bis-trigger]",
    ) as HTMLElement;

    this.bisPopupInitial = document.querySelector(
      `uwp-product-notify-me[data-product-id="${quickShopProduct.id}"]`,
    );
    const clone = this.bisPopupInitial?.cloneNode(true) as HTMLElement;
    if (clone) {
      this.bisPopup = document.body.appendChild(clone);
    }
    this.soldOutToggles?.forEach((toggle) => {
      toggle.addEventListener("click", this.handleSoldOutToggle.bind(this));
    });

    this.bisButton?.addEventListener("click", () => {
      closeQuickShopButton();
      this.bisPopup?.setAttribute("is-visible", "true");
    });
  }

  private handleSoldOutToggle = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    const target =
      (event.target as HTMLElement).closest("[data-sold-out-toggle]") ||
      (event.target as HTMLElement);

    this.soldOutToggles?.forEach((toggle) => {
      if (toggle === target && toggle.classList.contains("is-selected")) {
        toggle.classList.remove("is-selected");
        return;
      }
      toggle.classList.toggle("is-selected", toggle === target);
    });

    const isSelected = target.classList.contains("is-selected");

    if (!isSelected) {
      this.showATC();
      return;
    } else {
      this.atcButton?.classList.add("hidden");
      this.soldOutButton?.classList.remove("hidden");
    }
    const bisEnabled = target.hasAttribute("data-show-bis");

    if (!this.bisButton || !this.bisPopup) {
      return;
    }
    this.bisPopup?.setAttribute(
      "variant-id",
      target.getAttribute("data-variant-id") || "",
    );
    this.bisButton?.classList.toggle("hidden", !(isSelected && bisEnabled));
  };

  private showATC(): void {
    const selectedSoldOut = this.soldOutToggles?.find((toggle) =>
      toggle.classList.contains("is-selected"),
    );
    selectedSoldOut?.classList.remove("is-selected");
    const quickShopProduct = JSON.parse(
      this.getAttribute("data-product") || "{}",
    );
    if (!quickShopProduct.available) {
      return;
    }
    this.atcButton?.classList.remove("hidden");
    [this.soldOutButton, this.bisButton].forEach((button) =>
      button?.classList.add("hidden"),
    );
  }

  closeQuickShop() {
    if (this.quickShopModal) {
      document.body.removeChild(this.quickShopModal);
    }
  }
}

customElements.define("uwp-quickshop-button", QuickShopButton);
